/**
 * Person references: ids behind the scenes, nickname-or-first-name for display.
 * Real names live only in the people table (and free-text notes).
 */
import { useQuery } from '@tanstack/react-query'

import { api } from './client'
import { nameFromEmail } from '../components/format'

export interface Person {
  id: number
  name: string
  nickname: string
  group_name: string
  phone: string
  email: string
  notes: string
  sort: number
}

/** Nickname when set, else the first name — never a full name. */
export function personLabel(p: Person): string {
  const nick = (p.nickname ?? '').trim()
  if (nick) return nick
  const name = (p.name ?? '').trim()
  return name ? name.split(/\s+/)[0] : `#${p.id}`
}

export function usePeople() {
  return useQuery({
    queryKey: ['people'],
    queryFn: () => api.get<Person[]>('/v1/people'),
  })
}

export function usePersonLabels(): Record<number, string> {
  const { data } = usePeople()
  const labels: Record<number, string> = {}
  for (const p of data ?? []) labels[p.id] = personLabel(p)
  return labels
}

/** "Paul & Crista" / "Alex, Sam & Jo" — '' when no one is referenced. */
export function joinLabels(labels: Record<number, string>, ids: number[] | null | undefined): string {
  const names = (ids ?? []).map((id) => labels[id]).filter(Boolean)
  if (names.length === 0) return ''
  if (names.length === 1) return names[0]
  return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`
}

/** Display for an email identity (tasks/comments): roster nickname-or-first
 * name when the email matches a person, else the email local part. */
export function labelForEmail(people: Person[] | undefined, email: string | null | undefined): string {
  if (!email) return ''
  const match = (people ?? []).find(
    (p) => (p.email ?? '').trim().toLowerCase() === email.trim().toLowerCase(),
  )
  return match ? personLabel(match) : nameFromEmail(email)
}
